/**
 * Trasporto del chatbot di assistenza (n8n).
 *
 * NOTA: questo e' il chatbot di assistenza, una cosa distinta dall'AI Agent
 * (che gira su un servizio suo e risponde sui conti di trading).
 *
 * Estratto dal componente per poter essere testato: il guasto del 24/07 era
 * invisibile proprio perche' questa logica viveva dentro la UI e falliva senza
 * lasciare traccia.
 */

export const CHAT_REQUEST_TIMEOUT_MS = 60000;
export const CHAT_MAX_ATTEMPTS = 3;
export const CHAT_RETRY_BASE_DELAY_MS = 700;

/**
 * Errore non ritentabile: la richiesta e' malformata o il servizio non e'
 * configurato. Distinguerlo dai guasti temporanei evita tentativi inutili.
 *
 * ATTENZIONE: estendere `Error` non sopravvive alla transpilazione verso ES5
 * (il prototype si perde e `instanceof` torna false), quindi il riconoscimento
 * NON puo' basarsi su `instanceof`. Serve un marcatore esplicito: senza, un
 * 4xx verrebbe ritentato 3 volte pur dando sempre lo stesso esito.
 * Trovato da un test, non a occhio.
 */
export class ChatConfigError extends Error {
    public readonly isChatConfigError = true;

    constructor(message: string) {
        super(message);
        this.name = 'ChatConfigError';
        Object.setPrototypeOf(this, ChatConfigError.prototype);
    }
}

/** Riconosce l'errore non ritentabile senza dipendere da `instanceof`. */
export function isChatConfigError(error: unknown): error is ChatConfigError {
    return (
        typeof error === 'object' &&
        error !== null &&
        (error as { isChatConfigError?: boolean }).isChatConfigError === true
    );
}

/**
 * Legge l'endpoint del chatbot.
 *
 * Il valore e' iniettato a runtime dall'entrypoint del container, che sostituisce
 * il segnaposto `#ALGOTECH_ENV_N8N_CHAT_WEBHOOK_URL` nel bundle gia' compilato.
 * Due modi di essere "non configurato":
 *  - variabile non passata al container -> l'entrypoint lascia stringa vuota;
 *  - entrypoint non eseguito            -> resta il segnaposto con la `#`.
 * Entrambi vanno riconosciuti, altrimenti si tenta una POST verso l'URL "#...".
 */
export function getChatWebhookUrl(): string {
    const raw = process.env.ALGOTECH_ENV_N8N_CHAT_WEBHOOK_URL;

    if (!raw || raw.startsWith('#')) {
        return '';
    }

    return raw;
}

export function delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

export type ChatPayload = {
    chatInput: string;
    sessionId: string;
};

/**
 * Una singola POST, con timeout: senza `AbortController` una richiesta appesa
 * lascerebbe la chat a girare a vuoto per sempre.
 */
export async function postChatMessage(
    url: string,
    payload: ChatPayload,
): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(
        () => controller.abort(),
        CHAT_REQUEST_TIMEOUT_MS,
    );

    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal: controller.signal,
        });

        if (res.ok) {
            return res;
        }

        // 4xx: richiesta sbagliata, ritentarla darebbe lo stesso esito.
        if (res.status < 500) {
            const errorData = await res.json().catch(() => ({}));

            throw new ChatConfigError(
                errorData?.message || `Chat request failed (${res.status})`,
            );
        }

        throw new Error(`Chat service error (${res.status})`);
    } finally {
        clearTimeout(timeoutId);
    }
}

/**
 * POST con retry a backoff esponenziale sui soli guasti temporanei
 * (rete, timeout, 5xx). Ricorsiva perche' il progetto vieta i loop.
 */
export async function fetchWithRetry(
    url: string,
    payload: ChatPayload,
    tentativo = 1,
): Promise<Response> {
    try {
        return await postChatMessage(url, payload);
    } catch (error) {
        if (isChatConfigError(error)) {
            throw error;
        }

        const causa = error instanceof Error ? error : new Error(String(error));

        if (tentativo >= CHAT_MAX_ATTEMPTS) {
            // eslint-disable-next-line no-console
            console.error(
                `[chatbot] all ${CHAT_MAX_ATTEMPTS} attempts failed: ${causa.message}`,
            );

            throw causa;
        }

        // eslint-disable-next-line no-console
        console.warn(
            `[chatbot] attempt ${tentativo}/${CHAT_MAX_ATTEMPTS} failed: ` +
                `${causa.message}. Retrying...`,
        );

        await delay(CHAT_RETRY_BASE_DELAY_MS * 2 ** (tentativo - 1));

        return fetchWithRetry(url, payload, tentativo + 1);
    }
}
